/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICommerceApiSettings,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { BusinessPartner } from '@msdyn365-commerce/retail-proxy';
import { getManagedBusinessPartnersAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/BusinessPartnersDataActions.g';

import { buildCacheKey } from './utilities/utils';
import { QueryResultSettingsProxy } from './utilities/QueryResultSettingsProxy';

/**
 * Input for the get managed business partners data action.
 */
export class GetManagedBusinessPartnersInput implements IActionInput {
    private readonly apiSettings: ICommerceApiSettings;

    constructor(apiSettings: ICommerceApiSettings) {
        this.apiSettings = apiSettings;
    }

    public getCacheKey = () => buildCacheKey('ManagedBusinessPartners', this.apiSettings);

    public getCacheObjectType = () => 'BusinessPartner';

    public dataCacheType = (): CacheType => 'request';
}

export const createGetManagedBusinessPartnersInput = (inputData: ICreateActionContext): IActionInput => {
    if (!inputData.requestContext.user.isAuthenticated) {
        throw new Error('User is not logged in.');
    }

    return new GetManagedBusinessPartnersInput(inputData.requestContext.apiSettings);
};

/**
 * Gets the business partners managed by the current user.
 * @param input - Data action input.
 * @param context - Data action context.
 * @returns The managed business partners, or an empty list when the user is not a B2B contact.
 */
export async function getManagedBusinessPartners(
    input: GetManagedBusinessPartnersInput,
    context: IActionContext
): Promise<BusinessPartner[]> {
    // Managed business partners are only available to B2B contacts. Skip the call for everyone else so the
    // action resolves to an empty list instead of issuing an unnecessary (and failing) request on both SSR and CSR.
    if (!context.requestContext.user.isB2bContact) {
        return [];
    }

    return getManagedBusinessPartnersAsync({
        callerContext: context,
        queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context)
    });
}

export const getManagedBusinessPartnersDataAction = createObservableDataAction({
    id: 'get-managed-business-partners',
    action: <IAction<BusinessPartner[]>>getManagedBusinessPartners,
    input: createGetManagedBusinessPartnersInput
});

export default getManagedBusinessPartnersDataAction;
